import classNames from 'clsx';
import type { FormApi } from 'final-form';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { styles as sharedStyles } from '@swf/account';
import createForm from '@swf/components/Forms/Form';
import Text from '@swf/components/Inputs/Text';
import MitIdRedirect from '@swf/components/MitIdRedirect/MitIdRedirect';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import StarsInfo from '@swf/components/StarsInfo/StarsInfo';
import Translation from '@swf/components/Translation/Translation';
import { FeatureFlag, Site } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { TrackedTranslation } from '@swf/core/types/i18n';
import { scrollToTop } from '@swf/core/utils';
import { buildFormErrors } from '@swf/core/utils/forms';
import countries from '@swf/generated/countries.json';

import useMitId from '../hooks/useMitId';

import AddressFields from './AddressFields/AddressFields';
import AddressFieldsCZ from './AddressFieldsCZ/AddressFieldsCZ';
import {
  TXTCLI_Apply__forChanges,
  TXTCLI_Country,
  TXTCLI_Street_address_must_be_at_leas_ELL,
  TXTCLI_The_street_address_entered_app_ELL,
  TXTCLI_Wrong_Zip_format,
  TXTWCL_SorryX_you_must_verify_your_id_ELL,
  TXTWCL_The_city_you_have_entered_is_i_ELL_1
} from './AddressForm.messages.json';
import styles from './AddressForm.scss';
import SuccessResponseMessage from './SuccessResponseMessage/SuccessResponseMessage';

interface Props {
  className?: string;
  submitButtonText?: TrackedTranslation;
  successCallback?: () => void;
  hasCountryState?: boolean;
  disabled?: boolean;
}

interface FormValues {
  streetAddress?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  streetName?: string;
  buildingNumber?: string;
  orientationNumber?: string;
  region?: string;
  district?: string;
  partOfMunicipality?: string;
  extAuthToken?: string;
}

const errorTranslations = {
  TXTWCL_The_city_you_have_entered_is_i_ELL_1,
  TXTCLI_Street_address_must_be_at_leas_ELL,
  TXTCLI_The_street_address_entered_app_ELL,
  TXTCLI_Wrong_Zip_format
};

const restrictedCountries = ['AR'];
const restrictedStates = [
  'B', // Buenos Aires
  'C' // CABA
];

const Form = createForm<FormValues>();

const AddressForm: React.FC<Props> = ({
  className,
  submitButtonText = TXTCLI_Apply__forChanges,
  hasCountryState = true,
  successCallback,
  disabled
}) => {
  const site = useSelector(select.site.getId);
  const streetAddress = useSelector(select.account.getAddress);
  const buildingNumber = useSelector(select.account.getBuildingNumber);
  const streetName = useSelector(select.account.getStreetName);
  const city = useSelector(select.account.getCity);
  const zipCode = useSelector(select.account.getZipCode);
  const state = useSelector(select.account.getAddressState) as string;
  const country = useSelector(select.account.getCountry) as string;
  const euDeOasisStreetAddress = useFeature(FeatureFlag.EuDeOasisStreetAddress);
  const orientationNumber = useSelector(select.account.getOrientationNumber);
  const district = useSelector(select.account.getDistrict);
  const partOfMunicipality = useSelector(select.account.getPartOfMunicipality);
  const disableBuenosAiresFlag = useFeature(FeatureFlag.DisableBuenosAiresFeatures);
  const formApi = useRef<FormApi<FormValues>>();

  const {
    account: { updateAccount }
  } = useDispatch<Dispatch>();
  const { push } = useHistory();
  const { mitIdError, isMitIdRequired, mitIdData, mitIdSubmit } =
    useMitId<FormValues>(updateAccount);

  const onSubmit = (values: FormValues) =>
    mitIdSubmit({
      ...values,
      country,
      streetAddress:
        euDeOasisStreetAddress || isSiteCZ
          ? `${values.streetName} ${values.buildingNumber}`
          : values.streetAddress
    }).then((response) => {
      scrollToTop();

      if (response?.errors?.beError) {
        push('/notifications/egamesng-notification', {
          belgianRegulatorError: response.errors.beError
        });
        return;
      }
      const errors = response ? buildFormErrors(response, errorTranslations) : undefined;
      if (successCallback && !errors) {
        successCallback();
      }

      return errors;
    });

  useEffect(() => {
    if (mitIdData?.city && country) {
      formApi.current?.submit();
    }
  }, [country, mitIdData]);

  /* If in Buenos Aires, we should disable the State Field.
  We can turn this functionality on and off using the `DisableBuenosAiresFeatures` flag.
  If `DisableBuenosAiresFeatures` is true, state field will remain active. */

  const restricted =
    !disableBuenosAiresFlag &&
    restrictedCountries.includes(country) &&
    restrictedStates.includes(state);

  const isSiteCZ = site === Site.CZ;

  // TODO: generate .d.ts for countries.json
  const initialCountry = (countries as { value: string; text: string }[]).find(
    ({ value }) => value === country
  )?.text;

  const initialFormValues: FormValues = {
    streetAddress,
    streetName,
    buildingNumber,
    city,
    zip: zipCode,
    state,
    country: initialCountry,
    orientationNumber,
    district,
    partOfMunicipality
  } as FormValues;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={mitIdData || initialFormValues}
      className={className}
      render={({
        dirtySinceLastSubmit,
        form,
        hasSubmitErrors,
        pristine,
        submitError,
        submitSucceeded,
        submitting,
        values
      }) => {
        formApi.current = form;
        return (
          <>
            {isMitIdRequired && (
              <MitIdRedirect returnModal="/account/details/address/" values={values} />
            )}
            {submitSucceeded && !isMitIdRequired && <SuccessResponseMessage />}
            {mitIdError && (
              <StarsInfo type="error" className={sharedStyles.message}>
                <Translation {...TXTWCL_SorryX_you_must_verify_your_id_ELL} />
              </StarsInfo>
            )}
            {submitError && (
              <StarsInfo type="error" className={styles.message}>
                {submitError}
              </StarsInfo>
            )}
            {isSiteCZ ? (
              <AddressFieldsCZ disabled={restricted || disabled} />
            ) : (
              <AddressFields
                disabled={disabled}
                hasCountryState={hasCountryState}
                restricted={restricted}
              />
            )}
            {hasCountryState && <Text id="country" label={TXTCLI_Country} disabled />}
            <StarsButton
              block
              loading={submitting}
              disabled={pristine || (hasSubmitErrors && !dirtySinceLastSubmit)}
              type="submit"
              className={classNames(sharedStyles.submit)}
              translation={submitButtonText}
            />
          </>
        );
      }}
    />
  );
};

export default AddressForm;
